import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { mergeRefs } from 'use-callback-ref';

import IconButton from '@atlaskit/button/icon/button';
import { css } from '@atlaskit/css';
import { FULL_PAGE_EDITOR_TOOLBAR_HEIGHT } from '@atlaskit/editor-shared-styles/constants';
import EmojiResource from '@atlaskit/emoji/emoji-resource';
import ResourcedEmoji from '@atlaskit/emoji/resourced-emoji';
import TableOfContentsIcon from '@atlaskit/icon-lab/core/table-of-content';
import { Popup } from '@atlaskit/popup/popup';
import type { PopupProps, TriggerProps } from '@atlaskit/popup/types';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { tableOfContentsButtonMessages } from './tableOfContentsButtonMessages.ts';
import { TableOfContents } from './TableOfContents.tsx';
import type { TableOfContentsEmojiNode, TableOfContentsItem } from './tableOfContentsTypes.ts';
import { useEditorScrollSelection } from './useEditorScrollSelection.ts';
import { useRendererScrollSelection } from './useRendererScrollSelection.ts';

const DEFAULT_POPUP_OFFSET: [number, number] = [8, 16];
const SIDEBAR_POPUP_OFFSET: [number, number] = [0, 8];
const CONFLUENCE_HEADER_HEIGHT = '56px';
const TOC_EMOJI_HEIGHT = 16;

const buttonContainerStyles = css({
	display: 'inline-flex',
	position: 'relative',
});

const changeboardingIndicatorStyles = css({
	backgroundColor: token('color.background.accent.blue.bolder'),
	borderRadius: token('radius.full'),
	borderColor: token('color.border.inverse'),
	borderStyle: 'solid',
	borderWidth: token('border.width.selected'),
	display: 'block',
	height: '6px',
	width: '6px',
	pointerEvents: 'none',
	position: 'absolute',
	right: token('space.025'),
	top: token('space.025'),
});

const i18n = defineMessages({
	emptyToc: {
		id: 'floating-table-of-contents-common.empty-toc',
		description: 'The message to display when there are no headings in the page',
		defaultMessage: 'Add headings to navigate your content.',
	},
});

function getOutlineMaxHeight(isToolbarDocked: boolean): string {
	const toolbarHeight = isToolbarDocked ? FULL_PAGE_EDITOR_TOOLBAR_HEIGHT() : '0px';
	return `calc(60dvh - ${toolbarHeight} - ${CONFLUENCE_HEADER_HEIGHT})`;
}

function useRenderTableOfContentsEmoji(): (emoji: TableOfContentsEmojiNode) => React.ReactNode {
	const { cloudId, userId } = useSessionData();

	return useCallback(
		(emoji: TableOfContentsEmojiNode) => (
			<ResourcedEmoji
				emojiId={{ id: emoji.id, shortName: emoji.shortName, fallback: emoji.text }}
				emojiProvider={getEmojiProvider(EmojiResource, { cloudId, userId })}
				fitToHeight={TOC_EMOJI_HEIGHT}
				showTooltip
			/>
		),
		[cloudId, userId],
	);
}

function PopupPositioningReference({
	popupRef,
	positioningRef,
}: {
	popupRef: TriggerProps['ref'];
	positioningRef: React.RefObject<HTMLElement>;
}): null {
	useEffect(() => {
		// Popup positions from its trigger ref. Point that ref at the supplied container while
		// leaving the actual trigger button in its existing place in the DOM and tab order.
		const mergedPopupRef = mergeRefs([popupRef]);
		mergedPopupRef.current = positioningRef.current;
		return () => {
			mergedPopupRef.current = null;
		};
	}, [popupRef, positioningRef]);

	return null;
}

function PositionedPopupContent({
	children,
	contentRef,
	onHeightChange,
	positioningRef,
}: {
	children: React.ReactNode;
	contentRef: React.RefObject<HTMLDivElement>;
	onHeightChange: (popupContentHeight: number, positioningElementHeight: number) => void;
	positioningRef?: React.RefObject<HTMLElement>;
}): React.JSX.Element {
	useLayoutEffect(() => {
		const positioningElement = positioningRef?.current;
		const popupContent = contentRef.current;
		if (!positioningElement || !popupContent) {
			return;
		}

		const positioningElementHeight = positioningElement.getBoundingClientRect().height;
		const updateHeight = () => {
			onHeightChange(popupContent.getBoundingClientRect().height, positioningElementHeight);
		};

		updateHeight();
		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		const resizeObserver = new ResizeObserver(updateHeight);
		resizeObserver.observe(popupContent);
		return () => resizeObserver.disconnect();
	}, [contentRef, onHeightChange, positioningRef]);

	return <div ref={contentRef}>{children}</div>;
}

type FloatingTableOfContentsPopupButtonProps = {
	items: TableOfContentsItem[];
	onItemClicked?: (item: TableOfContentsItem) => void;
	onSelect: (item: TableOfContentsItem) => void;
	onOpenChange?: (isOpen: boolean) => void;
	isToolbarDocked?: boolean;
	popupPositioningRef?: React.RefObject<HTMLElement>;
	popupRepositionKey?: number;
	scrollMode?: 'editor' | 'renderer';
	showChangeboardingIndicator?: boolean;
};

export function FloatingTableOfContentsPopupButton({
	items,
	onItemClicked,
	onSelect,
	onOpenChange,
	isToolbarDocked = false,
	popupPositioningRef,
	popupRepositionKey,
	scrollMode = 'renderer',
	showChangeboardingIndicator = false,
}: FloatingTableOfContentsPopupButtonProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [sidebarPopupOffset, setSidebarPopupOffset] =
		useState<[number, number]>(SIDEBAR_POPUP_OFFSET);
	const triggerRef = useRef<HTMLElement | null>(null);
	const popupContentRef = useRef<HTMLDivElement | null>(null);
	const editorScrollSelectedHeadingId = useEditorScrollSelection(
		items,
		isOpen && scrollMode === 'editor',
	);
	const rendererScrollSelectedHeadingId = useRendererScrollSelection(
		items,
		isOpen && scrollMode === 'renderer',
		triggerRef,
	);
	const scrollSelectedHeadingId =
		scrollMode === 'editor' ? editorScrollSelectedHeadingId : rendererScrollSelectedHeadingId;
	const renderEmoji = useRenderTableOfContentsEmoji();
	const buttonLabel = formatMessage(tableOfContentsButtonMessages.buttonLabel);

	const updatePopupOffset = useCallback(
		(popupContentHeight: number, positioningElementHeight: number) => {
			// `left-end` bottom-aligns the popup; shift shorter content up to align the top instead.
			const verticalOffset = Math.min(popupContentHeight - positioningElementHeight, 0);
			setSidebarPopupOffset([verticalOffset, SIDEBAR_POPUP_OFFSET[1]]);
		},
		[],
	);

	const updateIsOpen = useCallback(
		(nextIsOpen: boolean) => {
			// Notify consumers first so content updates are batched into the popup's opening render.
			onOpenChange?.(nextIsOpen);
			setIsOpen(nextIsOpen);
		},
		[onOpenChange],
	);

	useEffect(() => {
		const positioningElement = popupPositioningRef?.current;
		if (!isOpen || !positioningElement) {
			return;
		}

		const handlePositioningElementClick = (event: MouseEvent) => {
			const eventPath = event.composedPath();
			const isTriggerClick = triggerRef.current && eventPath.includes(triggerRef.current);
			const isPopupContentClick =
				popupContentRef.current && eventPath.includes(popupContentRef.current);
			if (!isTriggerClick && !isPopupContentClick) {
				updateIsOpen(false);
			}
		};
		positioningElement.addEventListener('click', handlePositioningElementClick);
		return () => positioningElement.removeEventListener('click', handlePositioningElementClick);
	}, [isOpen, popupPositioningRef, updateIsOpen]);

	const handleTriggerClick = useCallback(() => {
		updateIsOpen(!isOpen);
	}, [isOpen, updateIsOpen]);

	const renderPopupContent: PopupProps['content'] = useCallback(
		() => (
			<PositionedPopupContent
				contentRef={popupContentRef}
				onHeightChange={updatePopupOffset}
				positioningRef={popupPositioningRef}
			>
				<TableOfContents
					emptyState={formatMessage(i18n.emptyToc)}
					items={items}
					label={buttonLabel}
					maxHeight={getOutlineMaxHeight(isToolbarDocked)}
					onItemClicked={onItemClicked}
					onSelect={onSelect}
					renderEmoji={renderEmoji}
					selectedHeadingId={scrollSelectedHeadingId}
				/>
			</PositionedPopupContent>
		),
		[
			buttonLabel,
			formatMessage,
			isToolbarDocked,
			items,
			onItemClicked,
			onSelect,
			popupPositioningRef,
			renderEmoji,
			scrollSelectedHeadingId,
			updatePopupOffset,
		],
	);

	const handlePopupClose: NonNullable<PopupProps['onClose']> = useCallback(
		(event) => {
			updateIsOpen(false);
			if (event?.type === 'keydown') {
				requestAnimationFrame(() => triggerRef.current?.focus());
			}
		},
		[updateIsOpen],
	);

	const renderPopupTrigger: PopupProps['trigger'] = useCallback(
		(triggerProps) => (
			<>
				{popupPositioningRef ? (
					<PopupPositioningReference
						popupRef={triggerProps.ref}
						positioningRef={popupPositioningRef}
					/>
				) : null}
				<Tooltip content={buttonLabel} position="left">
					<span css={buttonContainerStyles}>
						<IconButton
							{...triggerProps}
							appearance="subtle"
							ref={
								popupPositioningRef
									? // Popup's ref is redirected to the container; keep the button ref for focus.
										mergeRefs([triggerRef])
									: mergeRefs([triggerProps.ref, triggerRef])
							}
							icon={TableOfContentsIcon}
							isSelected={isOpen}
							label={buttonLabel}
							onClick={handleTriggerClick}
							testId="sidebar-table-of-contents-button"
						/>
						{showChangeboardingIndicator ? (
							<span
								aria-hidden="true"
								css={changeboardingIndicatorStyles}
								data-testid="floating-table-of-contents-changeboarding-indicator"
							/>
						) : null}
					</span>
				</Tooltip>
			</>
		),
		[buttonLabel, handleTriggerClick, isOpen, popupPositioningRef, showChangeboardingIndicator],
	);

	return (
		<Popup
			// Remount the positioning layer when an external layout change moves its reference.
			key={popupRepositionKey}
			autoFocus={false}
			label={buttonLabel}
			content={renderPopupContent}
			isOpen={isOpen}
			onClose={handlePopupClose}
			offset={popupPositioningRef ? sidebarPopupOffset : DEFAULT_POPUP_OFFSET}
			placement="left-end"
			shouldDisableFocusLock
			shouldReturnFocus={false}
			trigger={renderPopupTrigger}
		/>
	);
}
